import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiChevronRight, FiMapPin } from 'react-icons/fi';
import { citiesByState, stateNames, getAllStates } from '../data/cityData';

const StatePage = () => {
  const { stateCode } = useParams();
  const code = stateCode?.toUpperCase();
  const stateName = stateNames[code] || '';
  const cities = citiesByState[code] || [];
  const allStates = useMemo(() => getAllStates(), []);

  const seoTitle = `Sheds for Sale in ${stateName} — Compare Local Dealers & Prices`;
  const seoH1 = `Storage Sheds and Portable Buildings in ${stateName}`;
  const seoDescription = `Browse sheds for sale across ${cities.length} ${stateName} cities. Compare barn, gable and workshop styles from local dealers. Rent-to-own financing available statewide.`;
  const seoOgDescription = `${stateName} residents are finding better deals on sheds. See what's available in ${cities.length} cities from verified dealers.`;
  const canonicalUrl = `https://shedx.com/sheds-for-sale/state/${stateCode}`;

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: seoTitle,
    description: seoDescription,
    url: canonicalUrl,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://shedx.com/' },
        { '@type': 'ListItem', position: 2, name: 'Sheds', item: 'https://shedx.com/sheds' },
        { '@type': 'ListItem', position: 3, name: stateName, item: canonicalUrl }
      ]
    }
  };

  if (!stateName) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-5xl text-center py-20">
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">State Not Found</h1>
          <Link to="/sheds" className="text-sm text-gray-500 hover:text-gray-900">Browse all sheds &rarr;</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoOgDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-24 pb-16">
        {/* Hero */}
        <div className="bg-white border-b border-gray-200 py-10">
          <div className="container mx-auto px-4 max-w-5xl">
            <nav className="flex items-center gap-1.5 text-sm text-gray-400 mb-4" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-gray-600">Home</Link>
              <FiChevronRight className="w-3 h-3" />
              <Link to="/sheds" className="hover:text-gray-600">Sheds</Link>
              <FiChevronRight className="w-3 h-3" />
              <span className="text-gray-700">{stateName}</span>
            </nav>
            <h1 className="text-3xl font-semibold text-gray-900 mb-3">{seoH1}</h1>
            <p className="text-gray-500 max-w-2xl">
              Browse sheds for sale in {cities.length} {stateName} cities. Compare styles, sizes and prices from verified local dealers. Rent-to-own and free delivery available on select listings.
            </p>
          </div>
        </div>

        <div className="container mx-auto px-4 max-w-5xl py-8">
          {/* City Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {cities.map(city => (
              <Link
                key={city.slug}
                to={`/sheds-for-sale/${city.slug}`}
                className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-4 py-3.5 hover:border-gray-400 hover:shadow-sm transition-all group"
              >
                <FiMapPin className="w-4 h-4 text-gray-400 group-hover:text-gray-700 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 group-hover:underline truncate">{city.name}</p>
                  <p className="text-xs text-gray-400">Sheds for sale</p>
                </div>
              </Link>
            ))}
          </div>

          {/* SEO Content */}
          <div className="mt-12 space-y-5">
            <h2 className="text-xl font-semibold text-gray-900">Shed Buying Guide for {stateName}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {stateName} has a thriving market for portable buildings. From compact garden sheds to large detached workshops, local dealers build structures tailored to regional climate conditions. Most builders use treated lumber, LP SmartSide or vinyl siding rated for {stateName} weather patterns.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Pricing varies by size, materials and features. A basic 8x10 utility shed starts around $2,000. Mid-range 10x16 to 12x20 buildings with lofts and workbenches run $4,000 to $10,000. Premium workshops and garages with electrical, insulation and finished interiors can exceed $20,000.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Most {stateName} dealers offer rent-to-own programs that require no credit check. Terms run 36 to 60 months with the option to pay off early. Free delivery and professional setup are standard within 50 miles of the dealer lot.
            </p>
          </div>

          {/* Other States */}
          <div className="mt-12">
            <h3 className="text-sm text-gray-400 uppercase tracking-wider mb-3">Browse sheds in other states</h3>
            <div className="flex flex-wrap gap-2">
              {allStates
                .filter(s => s.code !== code)
                .slice(0, 20)
                .map(s => (
                  <Link
                    key={s.code}
                    to={`/sheds-for-sale/state/${s.code.toLowerCase()}`}
                    className="text-sm text-gray-600 hover:text-gray-900 border border-gray-200 px-3 py-1.5 hover:border-gray-300 transition-colors"
                  >
                    {s.name}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StatePage;
